/**
 * AdminCompanyUploads.js — the admin "Uploads" tab of a company (Lane 1430).
 *
 * Lists every file the company uploaded to chat (any status, newest first)
 * and lets a Wingman admin remove one for real. Removal is the same backend
 * function the buyer's Remove file button calls
 * (backend/helpers/upload_removal.py remove_upload). A confirmation names the
 * file first. Below the list: recent removals and whether the stored copy is
 * gone yet.
 */

import React, { useCallback, useEffect, useState } from 'react';
import colors from '../utils/adminColors';
import {
  adminRemoveUpload,
  listCompanyUploads,
  removalErrorText,
} from '../api/uploadRemoval';

const STORED_FILE_TEXT = {
  deleted: 'Deleted',
  none: 'No stored copy',
  kept_shared: 'Kept (another upload uses it)',
  pending: 'Not deleted yet, will retry',
  failed: 'Not deleted yet, will retry',
};

function formatDate(value) {
  if (!value) return '';
  const d = new Date(value);
  return Number.isNaN(d.getTime()) ? '' : d.toLocaleString();
}

function formatSize(bytes) {
  if (bytes == null) return '';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

const cell = {
  padding: '10px 12px', borderBottom: `1px solid ${colors.mediumGray}`,
  fontSize: '13px', color: colors.darkText, textAlign: 'left', verticalAlign: 'top',
};
const head = { ...cell, fontSize: '11px', fontWeight: 600, color: colors.textMuted };
const btn = {
  background: colors.white, border: `1px solid ${colors.mediumGray}`,
  color: colors.darkText, padding: '4px 10px', borderRadius: '6px',
  fontSize: '12px', fontWeight: 500, cursor: 'pointer', whiteSpace: 'nowrap',
};
const dangerBtn = {
  ...btn, background: colors.danger, borderColor: colors.danger, color: colors.white,
};

export default function AdminCompanyUploads({ companyId }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [confirming, setConfirming] = useState(null);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      setData(await listCompanyUploads(companyId));
    } catch (err) {
      setError(removalErrorText(err));
    }
  }, [companyId]);

  useEffect(() => { load(); }, [load]);

  const remove = async (upload) => {
    setBusy(true);
    setNotice(null);
    try {
      const res = await adminRemoveUpload(companyId, upload.candidate_id);
      setNotice({ ok: true, text: res.message });
    } catch (err) {
      setNotice({ ok: false, text: removalErrorText(err) });
    } finally {
      setBusy(false);
      setConfirming(null);
      load();
    }
  };

  if (error) {
    return (
      <div style={{ padding: '24px', color: colors.darkText }}>
        {error} <button type="button" style={btn} onClick={load}>Try again</button>
      </div>
    );
  }
  if (!data) return <div style={{ padding: '24px', color: colors.textMuted }}>Loading&hellip;</div>;

  const uploads = data.uploads || [];
  const removals = data.removals || [];

  return (
    <div style={{
      background: colors.white, border: `1px solid ${colors.mediumGray}`,
      borderRadius: '12px', padding: '20px',
    }}>
      <h3 style={{ fontSize: 'var(--font-size-md)', color: colors.darkText, margin: '0 0 4px' }}>
        Uploaded files
      </h3>
      <p style={{ fontSize: '13px', color: colors.textMuted, margin: '0 0 16px' }}>
        Files this company uploaded to chat, newest first
        {uploads.length >= data.limit ? ` (the newest ${data.limit})` : ''}.
        Removing a file deletes it, its stored table and its chat links, and
        Wingman stops using anything it learned from it.
      </p>

      {notice && (
        <div role="status" style={{
          marginBottom: '12px', padding: '10px 12px', borderRadius: '8px', fontSize: '13px',
          background: notice.ok ? '#ECFDF5' : '#FEF2F2',
          color: notice.ok ? colors.success : colors.danger,
        }}>
          {notice.text}
        </div>
      )}

      {uploads.length === 0 ? (
        <div style={{ padding: '24px 0', color: colors.textMuted, fontSize: '13px' }}>
          This company has no uploaded files.
        </div>
      ) : (
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <th style={head}>File</th>
              <th style={head}>Uploaded by</th>
              <th style={head}>When</th>
              <th style={head}>Size</th>
              <th style={head}>Status</th>
              <th style={head} aria-label="Actions" />
            </tr>
          </thead>
          <tbody>
            {uploads.map(u => (
              <React.Fragment key={u.candidate_id}>
                <tr>
                  <td style={{ ...cell, wordBreak: 'break-word' }}>{u.file_name}</td>
                  <td style={cell}>{u.uploaded_by || u.uploaded_by_email || 'Unknown'}</td>
                  <td style={cell}>{formatDate(u.created_at)}</td>
                  <td style={cell}>{formatSize(u.file_size_bytes)}</td>
                  <td style={cell}>{u.status}</td>
                  <td style={{ ...cell, textAlign: 'right' }}>
                    {confirming !== u.candidate_id && (
                      <button type="button" style={btn} disabled={busy}
                              onClick={() => { setNotice(null); setConfirming(u.candidate_id); }}>
                        Remove
                      </button>
                    )}
                  </td>
                </tr>
                {confirming === u.candidate_id && (
                  <tr>
                    <td colSpan={6} style={{ ...cell, background: '#FEF2F2' }}>
                      <div style={{ marginBottom: '8px' }}>
                        Remove &ldquo;{u.file_name}&rdquo; for everyone at this company?
                        This cannot be undone.
                      </div>
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <button type="button" style={dangerBtn} disabled={busy}
                                onClick={() => remove(u)}>
                          {busy ? 'Removing…' : 'Remove file'}
                        </button>
                        <button type="button" style={btn} disabled={busy}
                                onClick={() => setConfirming(null)}>
                          Keep it
                        </button>
                      </div>
                    </td>
                  </tr>
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      )}

      {removals.length > 0 && (
        <>
          <h3 style={{ fontSize: 'var(--font-size-base)', color: colors.darkText, margin: '24px 0 8px' }}>
            Removed files
          </h3>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={head}>File</th>
                <th style={head}>Removed by</th>
                <th style={head}>When</th>
                <th style={head}>Stored copy</th>
              </tr>
            </thead>
            <tbody>
              {removals.map(r => (
                <tr key={r.doc_upload_removal_id}>
                  <td style={{ ...cell, wordBreak: 'break-word' }}>{r.file_name}</td>
                  <td style={cell}>
                    {r.removed_by_email || (r.removed_by_kind === 'script' ? 'Script' : 'Unknown')}
                  </td>
                  <td style={cell}>{formatDate(r.removed_at)}</td>
                  <td style={cell}>{STORED_FILE_TEXT[r.r2_state] || r.r2_state}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </>
      )}
    </div>
  );
}
